import { create } from 'zustand';

/** 'notify' = a live-notification toast (someone else did something). Behaves
 *  exactly like 'info' — it exists so Toast can render the bell glyph from
 *  structure instead of call sites baking emoji into the message string. */
export type ToastKind = 'error' | 'info' | 'notify';

export interface ToastAction {
  label: string;
  run: () => void;
  /** This action reverses the write the toast announces — Toast renders it
   *  with the undo glyph. A flag, not label-sniffing: labels are copy. */
  undo?: true;
}

interface ShowOpts {
  /** Mark this toast a protected safety-net (the Undo toast). While it's up,
   *  routine toasts — including action-bearing live-notification toasts — won't
   *  displace it. Only another protected toast or an error can. */
  protect?: boolean;
}

interface ToastState {
  message: string | null;
  kind: ToastKind;
  action: ToastAction | null;
  show: (message: string, kind?: ToastKind, action?: ToastAction, opts?: ShowOpts) => void;
  clear: () => void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let protectedUntil = 0;

export const useToastStore = create<ToastState>()((set) => ({
  message: null,
  kind: 'info',
  action: null,
  show: (message, kind = 'info', action, opts) => {
    const now = Date.now();
    // A protected (Undo) toast is a safety net: nothing routine may displace it
    // in its window — NOT even an action-bearing notification toast. Only an
    // error or another protected toast gets through.
    if (now < protectedUntil && !opts?.protect && kind !== 'error') return;
    const ms = opts?.protect || action ? 6500 : 3500;
    protectedUntil = opts?.protect ? now + ms : (kind === 'error' ? 0 : protectedUntil);
    set({ message, kind, action: action ?? null });
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { protectedUntil = 0; set({ message: null, action: null }); }, ms);
  },
  clear: () => { protectedUntil = 0; set({ message: null, action: null }); },
}));
